import React, { useState } from "react";
import { RefreshCw, Calculator, History, AlertTriangle } from "lucide-react";
import ModuleNavigation from "../components/ModuleNavigation";

const CarbonFootprint = () => {
  const [activeSection, setActiveSection] = useState("what");
  const [isCompleted, setIsCompleted] = useState(false);

  const timelineData = [
    {
      year: 1750,
      event: "Pre-Industrial",
      co2: 280,
      description:
        "Atmospheric CO₂ levels were stable at ~280 ppm for thousands of years.",
    },
    {
      year: 1900,
      event: "Early Industrial",
      co2: 296,
      description:
        "Coal-powered steam engines and early factories begin increasing emissions.",
    },
    {
      year: 1950,
      event: "Post-War Boom",
      co2: 310,
      description:
        "Mass production of cars, suburbs, and consumer goods drives rapid emissions growth.",
    },
    {
      year: 1980,
      event: "Climate Awareness",
      co2: 338,
      description:
        "Scientists begin warning about potential climate impacts of rising CO₂.",
    },
    {
      year: 2000,
      event: "Digital Age",
      co2: 369,
      description:
        "Internet boom adds data centers while global manufacturing accelerates.",
    },
    {
      year: 2010,
      event: "Renewables Start",
      co2: 389,
      description:
        "Solar and wind begin scaling up, but fossil fuel use continues to grow globally.",
    },
    {
      year: 2020,
      event: "Pandemic Dip",
      co2: 412,
      description:
        "Temporary emissions drop during lockdowns, but quick rebound afterward.",
    },
    {
      year: 2025,
      event: "Present Day",
      co2: 422,
      description:
        "Current levels continue rising ~2-3 ppm annually despite clean energy growth.",
    },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case "what":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                What is a Carbon Footprint?
              </h2>
              <p className="text-gray-600 mb-4">
                A carbon footprint is the total amount of greenhouse gases
                (primarily carbon dioxide) released into the atmosphere as a
                result of the activities of an individual, organization, or
                community.
              </p>
              <p className="text-gray-600 mb-4">
                Carbon footprints are usually measured in tonnes of CO₂
                equivalent (CO₂e) per year, which includes other greenhouse
                gases like methane and nitrous oxide converted to the warming
                effect of CO₂.
              </p>
              <p className="text-gray-600">
                Understanding your carbon footprint is the first step toward
                reducing your impact on climate change.
              </p>
            </div>
            <div className="bg-white shadow-md rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Carbon Footprint Scopes
              </h3>

              <div className="relative mb-8">
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                <div className="relative pl-8 pb-6">
                  <div className="absolute left-0 w-6 h-6 rounded-full bg-primary-500 text-white flex items-center justify-center">
                    1
                  </div>
                  <h4 className="text-base font-medium text-gray-800 mb-1">
                    Scope 1: Direct Emissions
                  </h4>
                  <p className="text-sm text-gray-600">
                    Emissions from sources directly owned or controlled by you,
                    such as personal vehicle use or home heating.
                  </p>
                </div>

                <div className="relative pl-8 pb-6">
                  <div className="absolute left-0 w-6 h-6 rounded-full bg-primary-500 text-white flex items-center justify-center">
                    2
                  </div>
                  <h4 className="text-base font-medium text-gray-800 mb-1">
                    Scope 2: Indirect Emissions from Energy
                  </h4>
                  <p className="text-sm text-gray-600">
                    Emissions from electricity, steam, heat, or cooling
                    purchased for your use.
                  </p>
                </div>

                <div className="relative pl-8">
                  <div className="absolute left-0 w-6 h-6 rounded-full bg-primary-500 text-white flex items-center justify-center">
                    3
                  </div>
                  <h4 className="text-base font-medium text-gray-800 mb-1">
                    Scope 3: All Other Indirect Emissions
                  </h4>
                  <p className="text-sm text-gray-600">
                    Emissions from products you buy, services you use, food you
                    eat, and waste you produce.
                  </p>
                </div>
              </div>

              <div className="bg-primary-100 p-3 rounded-md">
                <p className="text-sm text-gray-700">
                  <strong>Did you know?</strong> For most individuals, Scope 3
                  emissions make up 60-80% of their total carbon footprint.
                </p>
              </div>
            </div>
          </div>
        );
      case "history":
        return (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Carbon Timeline (1750-2025)
            </h2>

            <div className="overflow-x-auto pb-4">
              <div className="min-w-max">
                <div className="flex items-end h-48 gap-4 mb-4">
                  {timelineData.map((data, index) => {
                    const heightPercent = (data.co2 / 422) * 100;
                    return (
                      <div key={index} className="flex flex-col items-center">
                        <div className="text-sm text-gray-500">
                          {data.co2} ppm
                        </div>
                        <div
                          className="w-10 bg-primary-500 rounded-t-sm"
                          style={{ height: `${heightPercent}%` }}
                        ></div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-4">
                  {timelineData.map((data, index) => (
                    <div key={index} className="w-10 text-center">
                      <div className="text-xs font-medium text-gray-700">
                        {data.year}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              {timelineData.map((data, index) => (
                <div
                  key={index}
                  className="bg-white shadow-sm rounded-lg p-4 border border-gray-200"
                >
                  <div className="text-sm font-semibold text-gray-700 mb-1">
                    {data.year}: {data.event}
                  </div>
                  <p className="text-xs text-gray-600">{data.description}</p>
                </div>
              ))}
            </div>
          </div>
        );
      case "calculate":
        return (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              How to Calculate a Carbon Footprint
            </h2>

            <div className="bg-white shadow-md rounded-lg p-6 mb-8">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                The Basic Formula
              </h3>

              <div className="bg-gray-100 p-4 rounded-lg mb-6 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-lg font-medium text-gray-800">
                    Carbon Footprint = Activity Data × Emission Factor
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    (what you did) × (emissions per unit of activity)
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-2">
                    Example: Driving a Car
                  </h4>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm text-gray-700">
                      <strong>Activity Data:</strong> 10,000 miles driven per
                      year
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Emission Factor:</strong> 0.4 kg CO₂e per mile
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Footprint:</strong> 10,000 × 0.4 = 4,000 kg CO₂e
                      (4 tonnes)
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-2">
                    Example: Electricity Use
                  </h4>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm text-gray-700">
                      <strong>Activity Data:</strong> 5,000 kWh per year
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Emission Factor:</strong> 0.5 kg CO₂e per kWh
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Footprint:</strong> 5,000 × 0.5 = 2,500 kg CO₂e
                      (2.5 tonnes)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-primary-100 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                Common Sources in Personal Carbon Footprints
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <li className="text-gray-700 flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                  Transportation (car, flights, public transit)
                </li>
                <li className="text-gray-700 flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                  Home energy use (electricity, heating, cooling)
                </li>
                <li className="text-gray-700 flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                  Food (especially meat and dairy)
                </li>
                <li className="text-gray-700 flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                  Consumer goods and services
                </li>
                <li className="text-gray-700 flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                  Waste generation
                </li>
                <li className="text-gray-700 flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                  Water use (heating water, treatment)
                </li>
              </ul>
            </div>
          </div>
        );
      case "limitations":
        return (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Limitations & Invisibility
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="bg-primary-700 text-white p-4">
                  <h3 className="font-medium flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Data Gaps
                  </h3>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 mb-4">
                    Carbon footprint calculations are limited by the data
                    available. Many emission factors are based on industry
                    averages or estimates rather than precise measurements.
                  </p>
                  <p className="text-gray-600 mb-4">
                    For example, the carbon footprint of many products doesn't
                    account for full lifecycle emissions from raw material
                    extraction through disposal.
                  </p>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm text-gray-700">
                      <strong>Example:</strong> When you calculate the footprint
                      of a smartphone, standard calculators might include
                      manufacturing and shipping, but often miss mining impacts,
                      R&D emissions, or data center energy needed to power apps.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="bg-primary-700 text-white p-4">
                  <h3 className="font-medium flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Invisible Gases
                  </h3>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 mb-4">
                    While carbon dioxide gets the most attention, other
                    greenhouse gases like methane, nitrous oxide, and various
                    industrial gases have much higher warming potentials.
                  </p>
                  <p className="text-gray-600 mb-4">
                    Methane is 28-36 times more potent than CO₂ over 100 years,
                    but it doesn't always get appropriate attention in carbon
                    footprint discussions.
                  </p>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm text-gray-700">
                      <strong>Example:</strong> Natural gas (primarily methane)
                      leaks throughout the supply chain are often underestimated
                      in footprint calculations, making it appear cleaner than
                      it actually is compared to other energy sources.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-primary-100 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 mr-2 text-primary-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Important Reminder
              </h3>
              <p className="text-gray-700">
                Despite these limitations, carbon footprints remain a useful
                tool for understanding and reducing your climate impact. The
                goal isn't perfect accounting, but meaningful action. Focus on
                the big sources of emissions first, and remember that systemic
                changes (like clean electricity grids and public transportation)
                amplify individual actions.
              </p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleMarkComplete = () => {
    setIsCompleted(!isCompleted);
    // In a real app, you would save this to localStorage or a backend
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-primary-600 mb-2">
          Carbon Footprint <span className="text-edu-orange">101</span>
        </h1>
        <div className="w-24 h-1 bg-edu-orange mx-auto mt-2 mb-6"></div>
      </div>

      <div className="bg-primary-100 p-6 rounded-lg mb-12">
        <p className="text-lg text-gray-800">
          Understanding carbon footprints helps us see how our everyday choices
          contribute to climate change. This section explains what they are, how
          they've evolved, and how to calculate your own impact.
        </p>
      </div>

      {/* Sub-navigation tabs */}
      <div className="flex flex-wrap border-b border-gray-200 mb-12">
        <button
          className={`flex items-center px-4 py-2 mr-4 ${
            activeSection === "what"
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection("what")}
        >
          <RefreshCw className="w-5 h-5 mr-2" />
          What is a Footprint?
        </button>
        <button
          className={`flex items-center px-4 py-2 mr-4 ${
            activeSection === "history"
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection("history")}
        >
          <History className="w-5 h-5 mr-2" />
          History Timeline
        </button>
        <button
          className={`flex items-center px-4 py-2 mr-4 ${
            activeSection === "calculate"
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection("calculate")}
        >
          <Calculator className="w-5 h-5 mr-2" />
          How to Calculate
        </button>
        <button
          className={`flex items-center px-4 py-2 ${
            activeSection === "limitations"
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection("limitations")}
        >
          <AlertTriangle className="w-5 h-5 mr-2" />
          Limitations
        </button>
      </div>

      {/* Content for selected section */}
      <div className="mb-16">{renderSection()}</div>

      {/* Module Navigation */}
      <ModuleNavigation
        prevLink="/case-studies"
        prevText="Previous: Case Studies"
        nextLink="/quiz"
        nextText="Next: Quiz"
        onMarkComplete={handleMarkComplete}
        isCompleted={isCompleted}
      />
    </div>
  );
};

export default CarbonFootprint;
